import { Link } from "react-router-dom";
import { ArrowUpRight, Home, Package, Phone } from "lucide-react";
import { FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const NotFound = () => {
  return (
    <div className="relative min-h-[80vh] overflow-hidden" data-testid="not-found-page">
      <PageMeta
        title="404 — Page Not Found"
        description="The page you're looking for doesn't exist. Browse our product catalog or contact the Pickleballgearz team."
      />

      {/* Background grid */}
      <div className="blueprint-grid pointer-events-none absolute inset-0" />

      <div className="relative mx-auto flex min-h-[80vh] max-w-[1400px] flex-col items-center justify-center px-5 py-24 text-center lg:px-10">
        {/* Giant 404 */}
        <FadeIn>
          <p className="pointer-events-none select-none font-display text-[28vw] font-semibold leading-none tracking-tighter text-black/[0.04] sm:text-[20vw] lg:text-[15vw]">
            404
          </p>
        </FadeIn>

        {/* Content overlay */}
        <div className="-mt-[8vw] relative z-10">
          <FadeIn delay={0.1}>
            <p className="mono-caption text-neutral-500">
              <span className="mr-2 inline-block h-2 w-2 bg-brand-red" />
              Page Not Found
            </p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tighter text-brand-ink sm:text-5xl md:text-6xl">
              THAT PAGE DOESN'T
              <br />
              EXIST
              <span className="text-brand-red">.</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.35}>
            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-neutral-500">
              The URL you visited may have moved, been removed, or never existed.
              Head back to browse our full product catalog or get in touch.
            </p>
          </FadeIn>

          <FadeIn delay={0.45}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/" className="btn-dark" data-testid="404-go-home">
                <Home size={16} /> Go Home
              </Link>
              <Link to="/products" className="btn-primary" data-testid="404-browse-products">
                Browse Products <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="404-contact">
                <Phone size={15} /> Contact Us
              </Link>
            </div>
          </FadeIn>
        </div>

        {/* Helpful links row */}
        <Reveal delay={0.5} className="mt-16 w-full max-w-2xl">
          <div className="grid gap-px border border-black/10 bg-black/10 sm:grid-cols-3">
            {[
              { label: "Products", desc: "Browse the catalog", to: "/products", icon: Package },
              { label: "B2B Programs", desc: "Wholesale & private label", to: "/b2b", icon: ArrowUpRight },
              { label: "Track Inquiry", desc: "Check your RFQ status", to: "/track", icon: ArrowUpRight },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group flex flex-col gap-1 bg-white p-6 transition-colors hover:bg-brand-ink"
                data-testid={`404-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="font-display text-lg font-medium tracking-tight group-hover:text-white">
                  {item.label}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-400 group-hover:text-white/50">
                  {item.desc}
                </span>
              </Link>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default NotFound;
